import { Injectable } from '@angular/core';

/**
 * Bridges the Angular app to the Android WebView native layer for network (ESC/POS) receipt
 * printing. A browser cannot open a raw TCP socket, so the Android host (which lives on the
 * same LAN as the printer) does the socket work and reports back asynchronously.
 *
 * Native contract (implemented in `TapToPayAndroid`):
 *  - `checkPrinter(host, port, timeoutMs, callbackId)` — probes reachability.
 *  - `printEscPos(base64Payload, host, port, callbackId)` — writes raw bytes to the printer.
 * Both resolve by calling `window.__androidPrinterCallback(callbackId, ok, error)`.
 */

type PrinterCallback = (callbackId: string, ok: boolean, error: string | null) => void;

interface AndroidPrinterBridge {
  checkPrinter?: (host: string, port: string, timeoutMs: string, callbackId: string) => void;
  printEscPos?: (base64Payload: string, host: string, port: string, callbackId: string) => void;
}

declare global {
  interface Window {
    __androidPrinterCallback?: PrinterCallback;
  }
}

/** Accesses the Android bridge without re-declaring the shared `window.TapToPayAndroid` global. */
function getAndroidBridge(): AndroidPrinterBridge | undefined {
  if (typeof window === 'undefined') return undefined;
  return (window as any as { TapToPayAndroid?: AndroidPrinterBridge }).TapToPayAndroid;
}

interface PendingRequest {
  resolve: (ok: boolean) => void;
  reject: (err: Error) => void;
  timer: ReturnType<typeof setTimeout>;
}

@Injectable({ providedIn: 'root' })
export class NativePrinterBridgeService {
  private readonly pending = new Map<string, PendingRequest>();
  private counter = 0;

  constructor() {
    if (typeof window !== 'undefined') {
      window.__androidPrinterCallback = (callbackId, ok, error) =>
        this.resolvePending(callbackId, ok, error);
    }
  }

  /** True when running inside the Android WebView with printer bridge methods available. */
  get isBridgeAvailable(): boolean {
    const bridge = getAndroidBridge();
    return typeof bridge?.checkPrinter === 'function' && typeof bridge?.printEscPos === 'function';
  }

  /**
   * Probes whether the printer is reachable on the LAN. Resolves `false` (never rejects) so
   * callers can simply fall back to PDF printing.
   */
  checkPrinter(host: string, port: number, timeoutMs: number): Promise<boolean> {
    const bridge = getAndroidBridge();
    if (typeof bridge?.checkPrinter !== 'function') {
      return Promise.resolve(false);
    }
    return new Promise<boolean>((resolve) => {
      const id = this.register(
        (ok) => resolve(ok),
        () => resolve(false),
        timeoutMs + 1500,
      );
      try {
        bridge.checkPrinter!(host, String(port), String(timeoutMs), id);
      } catch {
        this.clearPending(id);
        resolve(false);
      }
    });
  }

  /** Sends raw ESC/POS bytes (base64) to the printer. Rejects on failure so the caller can fall back. */
  printEscPos(base64Payload: string, host: string, port: number, timeoutMs: number): Promise<void> {
    const bridge = getAndroidBridge();
    if (typeof bridge?.printEscPos !== 'function') {
      return Promise.reject(new Error('Native printer bridge unavailable'));
    }
    return new Promise<void>((resolve, reject) => {
      const id = this.register(
        () => resolve(),
        (err) => reject(err),
        timeoutMs + 5000,
      );
      try {
        bridge.printEscPos!(base64Payload, host, String(port), id);
      } catch (err) {
        this.clearPending(id);
        reject(err instanceof Error ? err : new Error('Failed to invoke native printer'));
      }
    });
  }

  private register(
    onOk: (ok: boolean) => void,
    onFail: (err: Error) => void,
    timeoutMs: number,
  ): string {
    const id = `wayprint_${Date.now()}_${++this.counter}`;
    const timer = setTimeout(() => {
      if (this.pending.delete(id)) {
        onFail(new Error('Printer request timed out'));
      }
    }, Math.max(1000, timeoutMs));
    this.pending.set(id, { resolve: onOk, reject: onFail, timer });
    return id;
  }

  private resolvePending(callbackId: string, ok: boolean, error: string | null): void {
    const entry = this.pending.get(callbackId);
    if (!entry) return;
    this.pending.delete(callbackId);
    clearTimeout(entry.timer);
    if (ok) {
      entry.resolve(true);
    } else {
      entry.reject(new Error(error || 'Printer request failed'));
    }
  }

  private clearPending(id: string): void {
    const entry = this.pending.get(id);
    if (entry) {
      clearTimeout(entry.timer);
      this.pending.delete(id);
    }
  }
}
